import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, ListPlus, List, Search, ChevronDown, Menu, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavigationProps {
  showBackButton?: boolean;
}

const Navigation = ({ showBackButton }: NavigationProps = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<{name: string, url_slug: string}[]>([]);
  const [sports, setSports] = useState<{name: string, url_slug: string}[]>([]);
  const [platforms, setPlatforms] = useState<{name: string, url_slug: string}[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('name, url_slug')
          .order('name');
        
        if (categoriesError) throw categoriesError;
        setCategories(categoriesData || []);
        
        const { data: sportsData, error: sportsError } = await supabase
          .from('sports')
          .select('name, url_slug')
          .order('name');
        
        if (sportsError) throw sportsError;
        setSports(sportsData || []);
        
        const { data: platformsData, error: platformsError } = await supabase
          .from('platforms')
          .select('name, url_slug')
          .order('name');
        
        if (platformsError) throw platformsError;
        setPlatforms(platformsData || []);
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    
    fetchDropdownData();
  }, []);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });
      
      navigate("/");
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Error signing out",
        description: "There was an error signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleLogin = () => {
    navigate("/auth");
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setExpandedSection(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const renderDesktopNavigation = () => (
    <div className="flex items-center gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-1 hover:bg-gray-100 transition-colors">
            Categories
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-white max-h-[60vh] overflow-y-auto">
          <DropdownMenuItem 
            onSelect={() => navigate('/categories')}
            className="hover:bg-gray-100 transition-colors"
          >
            All Categories
          </DropdownMenuItem>
          {categories.map((category) => (
            <DropdownMenuItem 
              key={category.url_slug}
              onSelect={() => navigate(`/category/${category.url_slug}`)}
              className="hover:bg-gray-100 transition-colors"
            >
              {category.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-1 hover:bg-gray-100 transition-colors">
            Sports
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-white max-h-[60vh] overflow-y-auto">
          <DropdownMenuItem 
            onSelect={() => navigate('/sports')}
            className="hover:bg-gray-100 transition-colors"
          >
            All Sports
          </DropdownMenuItem>
          {sports.map((sport) => (
            <DropdownMenuItem 
              key={sport.url_slug}
              onSelect={() => navigate(`/sport/${sport.url_slug}`)}
              className="hover:bg-gray-100 transition-colors"
            >
              {sport.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-1 hover:bg-gray-100 transition-colors">
            Platforms
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-white max-h-[60vh] overflow-y-auto">
          <DropdownMenuItem 
            onSelect={() => navigate('/platforms')}
            className="hover:bg-gray-100 transition-colors"  
          >
            All Platforms
          </DropdownMenuItem>
          {platforms.map((platform) => (
            <DropdownMenuItem 
              key={platform.url_slug}
              onSelect={() => navigate(`/platform/${platform.url_slug}`)}
              className="hover:bg-gray-100 transition-colors"
            >
              {platform.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button 
        variant="ghost"
        onClick={() => navigate('/all-tools')}
        className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
      >
        <Search className="h-4 w-4" />
        All Tools
      </Button>

      {/* <Button 
        variant="ghost"
        onClick={() => navigate('/pricing')}
        className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
      >
        Pricing
      </Button> */}
      
      {!isLoading && isAuthenticated && (
        <>
          <Button 
            variant="ghost"
            onClick={() => navigate('/my-listings')}
            className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <List className="h-4 w-4" />
            My Listings
          </Button>
          
          <Button 
            variant="ghost"
            onClick={() => navigate('/submit-app')}
            className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <ListPlus className="h-4 w-4" />
            Submit App
          </Button>
        </>
      )}
      
      {!isLoading && (
        isAuthenticated ? (
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        ) : (
          <Button 
            variant="outline"
            onClick={handleLogin}
            className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <LogIn className="h-4 w-4" />
            Login
          </Button>
        )
      )}
    </div>
  );

  const renderMobileNavigation = () => (
    <div className="lg:hidden">
      <Button 
        variant="ghost" 
        size="icon" 
        className="ml-2" 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {mobileMenuOpen && (
        <div className="absolute top-16 left-0 right-0 bg-white border-b shadow-lg z-50 p-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col space-y-4">
            <div className="border-b pb-2">
              <Button
                variant="ghost"
                className="w-full justify-between text-start mb-2 font-semibold hover:bg-gray-100 transition-colors"
                onClick={() => toggleSection('categories')}
              >
                Categories
                <ChevronDown className={`h-4 w-4 transition-transform ${expandedSection === 'categories' ? 'rotate-180' : ''}`} />
              </Button>
              
              {expandedSection === 'categories' && (
                <div className="pl-2 space-y-1">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start hover:bg-gray-100 transition-colors"
                    onClick={() => handleNavigation('/categories')}
                  >
                    All Categories
                  </Button>
                  {categories.map((category) => (
                    <Button 
                      key={category.url_slug}
                      variant="ghost" 
                      className="w-full justify-start hover:bg-gray-100 transition-colors"
                      onClick={() => handleNavigation(`/category/${category.url_slug}`)}
                    >
                      {category.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-b pb-2">
              <Button
                variant="ghost"
                className="w-full justify-between text-start mb-2 font-semibold hover:bg-gray-100 transition-colors"
                onClick={() => toggleSection('sports')}
              >
                Sports
                <ChevronDown className={`h-4 w-4 transition-transform ${expandedSection === 'sports' ? 'rotate-180' : ''}`} />
              </Button>
              
              {expandedSection === 'sports' && (
                <div className="pl-2 space-y-1">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start hover:bg-gray-100 transition-colors"
                    onClick={() => handleNavigation('/sports')}
                  >
                    All Sports
                  </Button>
                  {sports.map((sport) => (
                    <Button 
                      key={sport.url_slug}
                      variant="ghost" 
                      className="w-full justify-start hover:bg-gray-100 transition-colors"
                      onClick={() => handleNavigation(`/sport/${sport.url_slug}`)}
                    >
                      {sport.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-b pb-2">
              <Button
                variant="ghost"
                className="w-full justify-between text-start mb-2 font-semibold hover:bg-gray-100 transition-colors"
                onClick={() => toggleSection('platforms')}
              >
                Platforms
                <ChevronDown className={`h-4 w-4 transition-transform ${expandedSection === 'platforms' ? 'rotate-180' : ''}`} />
              </Button>
              
              {expandedSection === 'platforms' && (
                <div className="pl-2 space-y-1">
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start hover:bg-gray-100 transition-colors"
                    onClick={() => handleNavigation('/platforms')}
                  >
                    All Platforms
                  </Button>
                  {platforms.map((platform) => (
                    <Button 
                      key={platform.url_slug}
                      variant="ghost" 
                      className="w-full justify-start hover:bg-gray-100 transition-colors"
                      onClick={() => handleNavigation(`/platform/${platform.url_slug}`)}
                    >
                      {platform.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <Button 
              variant="ghost"
              className="w-full justify-start hover:bg-gray-100 transition-colors"
              onClick={() => handleNavigation('/all-tools')}
            >
              <Search className="h-4 w-4 mr-2" />
              All Tools
            </Button>

            <Button 
              variant="ghost"
              className="w-full justify-start hover:bg-gray-100 transition-colors"
              onClick={() => handleNavigation('/pricing')}
            >
              Pricing
            </Button>
            
            {!isLoading && isAuthenticated && (
              <>
                <Button 
                  variant="ghost"
                  className="w-full justify-start hover:bg-gray-100 transition-colors"
                  onClick={() => handleNavigation('/my-listings')}
                >
                  <List className="h-4 w-4 mr-2" />
                  My Listings
                </Button>
                
                <Button 
                  variant="ghost"
                  className="w-full justify-start hover:bg-gray-100 transition-colors"
                  onClick={() => handleNavigation('/submit-app')}
                >
                  <ListPlus className="h-4 w-4 mr-2" />
                  Submit App
                </Button>
              </>
            )}
            
            {!isLoading && (
              isAuthenticated ? (
                <Button 
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button 
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleLogin}
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Login
                </Button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <nav className="bg-background border-b py-4 px-4 md:px-6 sticky top-0 w-full z-50">
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-xl font-bold">Fitness App Search</Link>
        </div>
        
        {isMobile ? renderMobileNavigation() : renderDesktopNavigation()}
      </div>
    </nav>
  );
};

export default Navigation;
